import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import PaymentMethodFields from '../../GestionHotel/MoteurReservation/components/PaymentMethodFields'
import { labelClass, inputClass } from '../shared'
import {
  getUserPaymentMethodOptionLabel,
} from './transferUserPaymentMethods'
import { loadTransferPaymentMethodsForPicker } from './transferPaymentModesLoader'
import { resolvePaymentMethodIcon } from './transferPaymentMethodIcon'

const readUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}')
  } catch {
    return {}
  }
}

/**
 * Physique : espèces (profil). Morale partenaire : payment-eligibility + profil.
 * Sinon : modes du profil connecté.
 */
const TransferPaymentPicker = ({
  totalAmount = 0,
  value,
  onChange,
  disabled = false,
  billingClientId = null,
  billingClientKind = null,
  billingPartnerId = null,
  checkInDate = null,
  variant = 'default',
}) => {
  const { t } = useTranslation()
  const onChangeRef = useRef(onChange)
  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const [methods, setMethods] = useState([])
  const [selectedId, setSelectedId] = useState(value?.methodId ?? null)
  const [amount, setAmount] = useState(
    () => value?.amount ?? (totalAmount != null ? String(totalAmount) : ''),
  )
  const [fields, setFields] = useState(() => value?.fields || {})

  const resolveMethodName = (id) => {
    const m = methods.find((x) => Number(x.id) === Number(id))
    return m ? getUserPaymentMethodOptionLabel(m, t) : ''
  }

  const push = (id, amt, f) => {
    const cb = onChangeRef.current
    if (id == null) {
      cb?.(null)
      return
    }
    const resolved = resolveMethodName(id)
    const fallback = value && String(value.methodName || '').trim()
    const methodName = resolved || fallback || ''
    cb?.({
      methodId: id,
      amount: Number(amt) || 0,
      fields: f || {},
      methodName,
    })
  }

  useEffect(() => {
    if (totalAmount != null && totalAmount !== '' && !value?.amount) {
      setAmount(String(totalAmount))
    }
  }, [totalAmount, value?.amount])

  useEffect(() => {
    let cancelled = false
    const uid = readUser()?.id
    if (!uid) {
      setMethods([])
      return undefined
    }

    ;(async () => {
      try {
        const list = await loadTransferPaymentMethodsForPicker({
          userId: uid,
          billingClientKind,
          billingClientId,
          billingPartnerId,
          checkInDateRaw: checkInDate,
          totalAmount,
        })
        if (cancelled) return

        setMethods(list)

        if (!list.length) {
          setSelectedId(null)
          setFields({})
          push(null)
          return
        }

        const pick = Number(list[0].id)
        setSelectedId(pick)
        const amt =
          totalAmount != null && totalAmount !== ''
            ? String(totalAmount)
            : ''
        setAmount(amt)
        setFields({})
        push(pick, amt, {})
      } catch {
        if (!cancelled) {
          setMethods([])
          push(null)
        }
      }
    })()

    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onChange volatil côté parent
  }, [billingClientId, billingClientKind, billingPartnerId, checkInDate, totalAmount])

  const method = methods.find((m) => Number(m.id) === Number(selectedId))

  const showEmptyHint =
    billingClientKind === 'physique' ||
    (billingClientKind === 'morale' && (billingClientId != null || billingPartnerId != null))

  const tamarra = variant === 'tamarra'

  const handleSelectMethod = (id) => {
    setSelectedId(id)
    setFields({})
    push(id, amount, {})
  }

  if (tamarra) {
    return (
      <div className="tamarra-pay">
        <h4 className="tamarra-grp">
          {t('tamarraBooking.payment.methodTitle', { defaultValue: 'Mode de paiement' })}
        </h4>
        {showEmptyHint && !methods.length ? (
          <p className="tamarra-bk-rate" role="status">
            {t('transferts.reservations.payment.emptyHint')}
          </p>
        ) : (
          <div
            className="tamarra-pay-tiles"
            role="radiogroup"
            aria-label={t('transferts.reservations.payment.method')}
          >
            {methods.map((m) => {
              const id = Number(m.id)
              const title = getUserPaymentMethodOptionLabel(m, t)
              const Icon = resolvePaymentMethodIcon(m, title)
              const selected = Number(selectedId) === id
              return (
                <label
                  key={m.id}
                  className={`tamarra-pay-tile${selected ? ' on' : ''}${disabled ? ' off' : ''}`}
                >
                  <input
                    type="radio"
                    name="tamarra-pay-method"
                    value={id}
                    checked={selected}
                    disabled={disabled}
                    onChange={() => handleSelectMethod(id)}
                  />
                  <span className="tamarra-pay-tile-ico" aria-hidden="true">
                    <Icon />
                  </span>
                  <b>{title}</b>
                </label>
              )
            })}
          </div>
        )}
        {method ? (
          <>
            <PaymentMethodFields
              paymentMethod={method}
              fieldValues={fields}
              onFieldChange={(f) => {
                setFields(f)
                push(selectedId, amount, f)
              }}
              disabled={disabled}
              amount={amount}
              amountMax={totalAmount}
              onAmountChange={(next) => {
                const capped =
                  totalAmount != null && Number(next) > Number(totalAmount)
                    ? Number(totalAmount)
                    : next
                setAmount(String(capped))
                push(selectedId, capped, fields)
              }}
              requireAmount={false}
              variant="tamarra"
            />
          </>
        ) : null}
      </div>
    )
  }

  return (
    <div className='space-y-3 rounded-[var(--radius-md)] border border-[var(--border)] bg-[color-mix(in_oklab,var(--muted)_40%,var(--card))] p-3'>
      <h4 className='text-sm font-semibold text-[var(--foreground)]'>
        {t('transferts.reservations.payment.methodsTitle')}
      </h4>
      <div>
        <span className={labelClass}>{t('transferts.reservations.payment.method')}</span>
        <select
          className={inputClass}
          disabled={disabled || !methods.length}
          value={selectedId ?? ''}
          onChange={(e) => {
            const v = e.target.value ? Number(e.target.value) : null
            setSelectedId(v)
            setFields({})
            push(v, amount, {})
          }}
        >
          {methods.map((m) => (
            <option key={m.id} value={m.id}>
              {getUserPaymentMethodOptionLabel(m, t)}
            </option>
          ))}
        </select>
        {showEmptyHint && !methods.length ? (
          <p className='mt-1 text-xs text-amber-700'>
            {t('transferts.reservations.payment.emptyHint')}
          </p>
        ) : null}
      </div>
      <div>
        <label className={labelClass} htmlFor='transfer_pay_amount'>
          {t('transferts.reservations.payment.amountLabel')}
        </label>
        <input
          id='transfer_pay_amount'
          type='number'
          min={0}
          step='0.01'
          className={inputClass}
          disabled={disabled}
          value={amount}
          onChange={(e) => {
            const v = e.target.value
            setAmount(v)
            push(selectedId, v, fields)
          }}
        />
      </div>
      {method ? (
        <PaymentMethodFields
          paymentMethod={method}
          fieldValues={fields}
          onFieldChange={(f) => {
            setFields(f)
            push(selectedId, amount, f)
          }}
          disabled={disabled}
          amount={amount}
          requireAmount={false}
        />
      ) : null}
    </div>
  )
}

export default TransferPaymentPicker
